import type { LegalSection } from './legalMeta';
import { LEGAL_META } from './legalMeta';

export const privacyPolicyTitle = 'Privacy Policy';

export const privacyPolicySections: LegalSection[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    paragraphs: [
      `This Privacy Policy explains how ${LEGAL_META.brandMark} (the “App”), our website ${LEGAL_META.siteName}, and related services (collectively, the “Services”) collect, use, disclose, and safeguard your information.`,
      `${LEGAL_META.operatorLine}`,
      `By using the Services, you agree to this Privacy Policy. If you do not agree, please do not use the Services.`,
      `Contact: ${LEGAL_META.privacyEmail}`,
    ],
  },
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    paragraphs: ['We collect the following categories of information:'],
    listItems: [
      'Account information: email address, password (stored in hashed form), and verification codes when you sign up or sign in.',
      'Profile information: username, name, optional bio, optional date of birth, profile photo, and cover photo.',
      'Content you provide: photos, captions, tags, comments, likes, saves, and posts you create.',
      'Location information: precise GPS coordinates and place details (such as address, city, country, and place identifiers) when you tag a memory or use map and place search features, if you grant permission.',
      'Social and usage data: interactions such as follows, likes, and comments; app version and platform sent with API requests.',
      'Device storage: authentication tokens and preferences (such as language and theme) stored on your device.',
      'Waitlist (website only): email address, optional name, and data processed by Google reCAPTCHA when you join our launch list.',
      'Social sign-in: if you use Google or Apple sign-in, we receive tokens and profile information allowed by those providers (such as email and name).',
    ],
  },
  {
    id: 'how-we-use',
    title: 'How We Use Information',
    paragraphs: ['We use information to:'],
    listItems: [
      'Provide, maintain, and improve the Services, including hosting your posts and profile.',
      'Authenticate you and secure your account.',
      'Enable location tagging, place search, and maps features.',
      'Operate social features such as comments, likes, follows, and sharing.',
      'Respond to support requests and enforce our Terms and Community Guidelines.',
      'Comply with law and protect the safety of users and the public.',
    ],
  },
  {
    id: 'legal-bases',
    title: 'Legal Bases (EEA, UK, and Similar Laws)',
    paragraphs: [
      'Where the GDPR or UK GDPR applies, we rely on the following legal bases:',
    ],
    listItems: [
      'Contract: processing necessary to provide the Services you request (account, posts, social features).',
      'Legitimate interests: security, fraud prevention, service improvement, and moderation, balanced against your rights.',
      'Consent: location permissions, waitlist marketing (where applicable), and non-essential cookies on our website.',
      'Legal obligation: when required to comply with applicable law.',
    ],
  },
  {
    id: 'sharing',
    title: 'Sharing and Service Providers',
    paragraphs: [
      'We do not sell your personal information. We share information only as described below:',
    ],
    listItems: [
      'Other users: content you make public (such as posts, comments, username, and profile fields) is visible to other users according to product design.',
      'Infrastructure: cloud hosting and storage providers (such as Amazon Web Services) that process data on our behalf under contractual safeguards.',
      'Google: Sign-In and server-side Google Maps / Places services used to power place search and maps (your device uses native maps; location and search queries are sent to our servers).',
      'Apple: Sign in with Apple when you choose that option on iOS.',
      'Meta / Facebook: optional sharing of images to Instagram or Facebook Stories when you initiate share from the App (we use an app identifier for that integration).',
      'Legal and safety: when required by law, to protect rights and safety, or to respond to valid legal process.',
    ],
  },
  {
    id: 'no-tracking',
    title: 'Analytics and Advertising',
    paragraphs: [
      'The App does not use third-party advertising SDKs or cross-app tracking for ads. We do not use Firebase Analytics, Google Analytics, or similar analytics SDKs in the mobile App as of the Last Updated date above.',
      'Our website waitlist may use Google reCAPTCHA for fraud prevention; see our Cookie Policy.',
    ],
  },
  {
    id: 'retention',
    title: 'Data Retention',
    paragraphs: [
      'We retain personal information while your account is active and as needed to provide the Services.',
      'When you delete your account in the App (Settings → Account → Delete account), we delete or de-identify your profile, posts, and associated personal data within a reasonable period, except where we must retain information for legal compliance, dispute resolution, or security (such as short-term backups).',
      'See our Account Deletion page for more detail.',
    ],
  },
  {
    id: 'security',
    title: 'Security',
    paragraphs: [
      'We use HTTPS for data in transit and industry-standard practices to protect stored data. Authentication tokens on mobile devices are stored using secure storage where supported.',
      'No method of transmission or storage is completely secure; we cannot guarantee absolute security.',
    ],
  },
  {
    id: 'rights',
    title: 'Your Rights and Choices',
    paragraphs: ['Depending on where you live, you may have rights to:'],
    listItems: [
      'Access, correct, or delete your personal information.',
      'Object to or restrict certain processing.',
      'Request portability of information you provided.',
      'Withdraw consent where processing is based on consent (such as location permissions via device settings).',
      'Delete your account in the App at any time.',
    ],
  },
  {
    id: 'europe',
    title: 'European Economic Area and United Kingdom',
    paragraphs: [
      `Data controller: ${LEGAL_META.dataControllerName} (${LEGAL_META.privacyEmail}).`,
      'If you are in the EEA or UK, you may lodge a complaint with your local supervisory authority. UK residents may contact the ICO (ico.org.uk). EEA residents may contact their national data protection authority (see edpb.europa.eu).',
      'We respond to privacy requests within one month where GDPR applies, unless extension is permitted.',
      'International transfers: your information may be processed in the United States and by providers globally. We rely on appropriate safeguards such as Standard Contractual Clauses where required.',
    ],
  },
  {
    id: 'children',
    title: "Children's Privacy",
    paragraphs: [
      `The Services are not directed to children under ${LEGAL_META.minimumAge}. You must be at least ${LEGAL_META.minimumAge} years old to use the Services.`,
      'We do not knowingly collect personal information from children under 13. If we learn that we have collected such information, we will delete it promptly. Contact us at ' +
        LEGAL_META.privacyEmail +
        '.',
      'In some EU/UK countries, users aged 13–15 (or up to 16) may need parental consent for online services under local law. Users in those jurisdictions must comply with applicable local requirements.',
    ],
  },
  {
    id: 'california',
    title: 'California Privacy Rights (CCPA/CPRA)',
    paragraphs: [
      'California residents have additional rights under the CCPA/CPRA, including the right to know categories collected, request deletion, and correct inaccurate information.',
      'We do not sell personal information. We do not share personal information for cross-context behavioral advertising as defined under California law based on our current practices.',
      'To exercise rights, use in-app account deletion or email ' + LEGAL_META.privacyEmail + '. We will not discriminate against you for exercising privacy rights.',
    ],
  },
  {
    id: 'international',
    title: 'International Users',
    paragraphs: [
      'If you access the Services from outside the United States, your information may be transferred to and processed in the United States and other countries where we or our providers operate.',
    ],
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. We will post the updated policy on this page and update the “Last Updated” date. Material changes may also be communicated in the App or by email where appropriate.',
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    paragraphs: [
      `Questions about this Privacy Policy or privacy requests: ${LEGAL_META.privacyEmail}`,
      `${LEGAL_META.legalEntityName} — ${LEGAL_META.contactEmail}`,
    ],
  },
];
